import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCopy,
  faDesktop,
  faFloppyDisk,
  faMobileScreen,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { useToast } from "../context/ToastContext";
import {
  Button,
  ErrorState,
  Field,
  PageHeader,
  Skeleton,
} from "../components/UI";
import ChatbotPreview from "../chatbot/ChatbotPreview";
import { getPublicAppUrl } from "../utils/publicUrl";

const defaults = {
  name: "Website Assistant",
  welcomeMessage: "Hi! How can I help you?",
  placeholder: "Ask a question…",
  primaryColor: "#556ee6",
  position: "bottom-right",
  avatar: "",
  theme: "light",
  model: "gpt-4o-mini",
  showBranding: true,
  systemPrompt: "",
};
export default function ChatbotPage() {
  const { websiteId } = useParams();
  const toast = useToast();
  const [site, setSite] = useState(null);
  const [config, setConfig] = useState(defaults);
  const [mode, setMode] = useState("desktop");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/websites/${websiteId}`)
      .then((r) => {
        const website = getData(r).website;
        setSite(website);
        setConfig({ ...defaults, ...website.chatbotConfig });
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [websiteId]);
  useEffect(load, [load]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = getData(
        await api.patch(`/websites/${websiteId}`, { chatbotConfig: config }),
      );
      setSite(data.website);
      toast.show("Chatbot configuration saved.");
    } catch (err) {
      toast.show(err.message, "error");
    } finally {
      setBusy(false);
    }
  };
  if (loading) return <Skeleton rows={8} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  const base = getPublicAppUrl();
  const snippet = `<script src="${base}/chatbot.js" data-website-id="${websiteId}"></script>`;
  const set = (key, value) => setConfig({ ...config, [key]: value });
  return (
    <>
      <PageHeader
        eyebrow="Chatbot studio"
        title={`${site.name} assistant`}
        description="Tune the visitor experience and review changes in a live visual preview."
        actions={
          <Button icon={faFloppyDisk} onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save configuration"}
          </Button>
        }
      />
      <form className="config-layout" onSubmit={save}>
        <section className="config-column">
          <div className="panel form-section">
            <header>
              <span className="section-number">01</span>
              <div>
                <h2>Identity & greeting</h2>
                <p>How the assistant introduces itself.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field label="Bot name">
                <input
                  value={config.name}
                  onChange={(e) => set("name", e.target.value)}
                  maxLength="80"
                  required
                />
              </Field>
              <Field label="Avatar URL" hint="Optional HTTPS image URL.">
                <input
                  type="url"
                  placeholder="https://…"
                  value={config.avatar}
                  onChange={(e) => set("avatar", e.target.value)}
                />
              </Field>
              <Field className="field-full" label="Welcome message">
                <textarea
                  rows="3"
                  value={config.welcomeMessage}
                  onChange={(e) => set("welcomeMessage", e.target.value)}
                  maxLength="500"
                />
              </Field>
              <Field className="field-full" label="Input placeholder">
                <input
                  value={config.placeholder}
                  onChange={(e) => set("placeholder", e.target.value)}
                  maxLength="120"
                />
              </Field>
            </div>
          </div>
          <div className="panel form-section">
            <header>
              <span className="section-number">02</span>
              <div>
                <h2>Appearance</h2>
                <p>Quietly on-brand, across screen sizes.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field label="Primary color">
                <div className="color-input">
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => set("primaryColor", e.target.value)}
                  />
                  <input
                    pattern="^#[0-9A-Fa-f]{6}$"
                    value={config.primaryColor}
                    onChange={(e) => set("primaryColor", e.target.value)}
                  />
                </div>
              </Field>
              <Field label="Position">
                <select
                  value={config.position}
                  onChange={(e) => set("position", e.target.value)}
                >
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                </select>
              </Field>
              <Field label="Theme">
                <select
                  value={config.theme}
                  onChange={(e) => set("theme", e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Match visitor device</option>
                </select>
              </Field>
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={config.showBranding}
                  onChange={(e) => set("showBranding", e.target.checked)}
                />
                <span />
                <div>
                  <strong>Show FlowChat branding</strong>
                  <small>Display a discreet attribution.</small>
                </div>
              </label>
            </div>
          </div>
          <div className="panel form-section">
            <header>
              <span className="section-number">03</span>
              <div>
                <h2>Model & guidance</h2>
                <p>
                  The connected administrator Puter account runs the model
                  server-side. Visitors never authenticate with Puter.
                </p>
              </div>
            </header>
            <div className="form-grid">
              <Field className="field-full" label="AI model">
                <select
                  value={config.model}
                  onChange={(e) => set("model", e.target.value)}
                >
                  <option value="gpt-4o-mini">GPT-4o mini</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                </select>
              </Field>
              <Field
                className="field-full"
                label="Public behavior guidance"
                hint="Optional tone and style guidance kept in the server-side request. It is never exposed to visitors, and core knowledge boundaries cannot be overridden."
              >
                <textarea
                  rows="5"
                  maxLength="2000"
                  placeholder="Use a concise, reassuring tone…"
                  value={config.systemPrompt}
                  onChange={(e) => set("systemPrompt", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </section>
        <aside className="preview-column">
          <div className="preview-toolbar">
            <div>
              <span className="eyebrow">Live preview</span>
              <strong>
                {mode === "desktop" ? "Desktop visitor" : "Mobile visitor"}
              </strong>
            </div>
            <div>
              <button
                type="button"
                className={mode === "desktop" ? "active" : ""}
                onClick={() => setMode("desktop")}
                aria-label="Desktop preview"
              >
                <FontAwesomeIcon icon={faDesktop} />
              </button>
              <button
                type="button"
                className={mode === "mobile" ? "active" : ""}
                onClick={() => setMode("mobile")}
                aria-label="Mobile preview"
              >
                <FontAwesomeIcon icon={faMobileScreen} />
              </button>
            </div>
          </div>
          <ChatbotPreview config={config} mobile={mode === "mobile"} />
          <div className="panel install-mini">
            <span className="eyebrow">Ready to install</span>
            <p>Save changes, then add this snippet to an allowed domain.</p>
            <div className="code-block">
              <code>{snippet}</code>
              <button
                type="button"
                aria-label="Copy installation snippet"
                onClick={async () => {
                  await navigator.clipboard.writeText(snippet);
                  toast.show("Installation snippet copied.");
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
            <small>
              <FontAwesomeIcon icon={faCheck} /> Domain verification is enforced
              by the server.
            </small>
          </div>
        </aside>
      </form>
    </>
  );
}
